import Immutable from 'immutable';
import moment from 'moment';
import User from 'sharedreact/data/User';
import { translate } from 'sharedreact/utils/LanguageSupport';
import Filter from 'sharedreact/data/Filter';
import FilterRule from 'sharedreact/data/FilterRule';

function _insertStateRules (
  rules: Immutable.List<FilterRule>,
  ruleIndex: number,
  currentRule: FilterRule,
  states: {name: string, value: string}[]
): {
  rules: Immutable.List<FilterRule>,
  index: number
} {
  let extendedRules: Immutable.List<FilterRule> = rules;

  for (const state of states) {
    if (state.name.toLowerCase().includes(currentRule.value?.toLowerCase() ?? '')) {
      ruleIndex++;
      extendedRules = extendedRules.insert(ruleIndex, FilterRule.createRule('state', state.value, FilterRule.IS));
      ruleIndex++;
      extendedRules = extendedRules.insert(ruleIndex, FilterRule.createOR());
    }
  }

  return {rules: extendedRules, index: ruleIndex};
}

function _expandStateRule (
  rules: Immutable.List<FilterRule>,
  ruleIndex: number,
  currentRule: FilterRule,
  states: {name: string, value: string}[]
): {
  rules: Immutable.List<FilterRule>,
  index: number
} {
  let extendedRules: Immutable.List<FilterRule> = rules;

  let newStateRules: Immutable.List<FilterRule> = Immutable.List();
  for (const state of states) {
    if (state.name.toLowerCase().includes(currentRule.value?.toLowerCase() ?? '')) {
      newStateRules = newStateRules.push(FilterRule.createRule('state', state.value, FilterRule.IS));
      if (newStateRules.size > 1) {
        newStateRules = newStateRules.push(FilterRule.createOR());
      }
    }
  }

  if (newStateRules.size > 0) {
    extendedRules = extendedRules.set(ruleIndex, newStateRules.first());
    for (let newStateRulesIndex = 1; newStateRulesIndex < newStateRules.size; newStateRulesIndex++) {
      ruleIndex++;
      extendedRules = extendedRules.insert(ruleIndex, newStateRules.get(newStateRulesIndex)!);
    }
  }

  return {rules: extendedRules, index: ruleIndex};
}

function _dateRangeFromString (stringValue: string): {from: moment.Moment, until: moment.Moment} | null {
  switch (stringValue.toLowerCase()) {
    case translate('shared.filter.keyword.today').toLowerCase():
    case 'today': {
      const todayStart: moment.Moment = moment(new Date()).set({'hour': 0, 'minute': 0, 'second': 0, 'millisecond': 0});
      const todayEnd: moment.Moment = todayStart.clone().add(1, 'd');

      return {from: todayStart, until: todayEnd};
    }
    case translate('shared.filter.keyword.yesterday').toLowerCase():
    case 'yesterday': {
      const yesterdayEnd: moment.Moment = moment(new Date()).set({'hour': 0, 'minute': 0, 'second': 0, 'millisecond': 0});
      const yesterdayStart: moment.Moment = yesterdayEnd.clone().subtract(1, 'd');

      return {from: yesterdayStart, until: yesterdayEnd};
    }
    case translate('shared.filter.keyword.tomorrow').toLowerCase():
    case 'tomorrow': {
      const tomorrowStart: moment.Moment = moment(new Date()).add(1, 'd').set({'hour': 0, 'minute': 0, 'second': 0, 'millisecond': 0});
      const tomorrowEnd: moment.Moment = tomorrowStart.clone().add(1, 'd');

      return {from: tomorrowStart, until: tomorrowEnd};
    }
  }

  // try to parse as a day with month, year and time:
  const dateTimeValue: moment.Moment = moment(stringValue, ['LLL', 'lll', 'LLLL', 'llll'], moment.locale(), true);
  if (dateTimeValue.isValid()) {
    const start: moment.Moment = dateTimeValue;
    const end: moment.Moment = start.clone().add(1, 'm');

    return {from: start, until: end};
  }

  // try to parse as a day with month and year:
  const valueDate: moment.Moment = moment(stringValue, ['L', 'l', 'LL', 'll'], moment.locale(), true);
  if (valueDate.isValid()) {
    const start: moment.Moment = valueDate;
    const end: moment.Moment = start.clone().add(1, 'd');

    return {from: start, until: end};
  }

  // try to parse as a month:
  const monthDate: moment.Moment = moment(stringValue, ['MMMM', 'MMM'], moment.locale(), true);
  if (monthDate.isValid()) {
    const start: moment.Moment = monthDate;
    const end: moment.Moment = start.clone().add(1, 'M');

    return {from: start, until: end};
  }

  return null;
}

function _insertDateRules (
  rules: Immutable.List<FilterRule>,
  ruleIndex: number,
  currentRule: FilterRule,
  key: string
): {
  rules: Immutable.List<FilterRule>,
  index: number
} {
  let extendedRules: Immutable.List<FilterRule> = rules;

  if (currentRule.value === translate('shared.filter.keyword.never').toLowerCase() || currentRule.value === 'never') {
    ruleIndex++;
    extendedRules = extendedRules.insert(ruleIndex, FilterRule.createRule(key, null, FilterRule.ISEMPTY));
    ruleIndex++;
    extendedRules = extendedRules.insert(ruleIndex, FilterRule.createOR());
  } else {
    const range: {from: moment.Moment, until: moment.Moment} | null = _dateRangeFromString(currentRule.value ?? '');

    if (range) {
      const fromString: string = range.from.format('YYYY-MM-DDTHH:mm:ss.SSSZ');
      const untilString: string = range.until.format('YYYY-MM-DDTHH:mm:ss.SSSZ');

      ruleIndex++;
      extendedRules = extendedRules.insert(ruleIndex, FilterRule.createRule(key, fromString, FilterRule.FROM));
      ruleIndex++;
      extendedRules = extendedRules.insert(ruleIndex, FilterRule.createRule(key, untilString, FilterRule.UNTIL));
      ruleIndex++;
      extendedRules = extendedRules.insert(ruleIndex, FilterRule.createAND());
      ruleIndex++;
      extendedRules = extendedRules.insert(ruleIndex, FilterRule.createOR());
    }
  }

  return {rules: extendedRules, index: ruleIndex};
}

function _expandDateRule (rules: Immutable.List<FilterRule>, ruleIndex: number, currentRule: FilterRule, key: string): {rules: Immutable.List<FilterRule>, index: number} {
  let extendedRules: Immutable.List<FilterRule> = rules;

  if (currentRule.value === translate('shared.filter.keyword.never').toLowerCase() || currentRule.value === 'never') {
    extendedRules = extendedRules.set(ruleIndex, FilterRule.createRule(key, null, FilterRule.ISEMPTY));
    ruleIndex++;
    extendedRules = extendedRules.insert(ruleIndex, FilterRule.createOR());
  } else {
    const range: {from: moment.Moment, until: moment.Moment} | null = _dateRangeFromString(currentRule.value ?? '');

    if (range) {
      const fromString: string = range.from.format('YYYY-MM-DDTHH:mm:ss.SSSZ');
      const untilString: string = range.until.format('YYYY-MM-DDTHH:mm:ss.SSSZ');

      extendedRules = extendedRules.set(ruleIndex, FilterRule.createRule(key, fromString, FilterRule.FROM));
      ruleIndex++;
      extendedRules = extendedRules.insert(ruleIndex, FilterRule.createRule(key, untilString, FilterRule.UNTIL));
      ruleIndex++;
      extendedRules = extendedRules.insert(ruleIndex, FilterRule.createAND());
    }
  }

  return {rules: extendedRules, index: ruleIndex};
}

function getCompareStringsFunction (operator: string): (name: string, value: string) => boolean {
  if (operator === FilterRule.IS) {
    return (name: string, value: string): boolean => (name === value);
  }
  return (name: string, value: string): boolean => (name.includes(value));
}

function _insertUserRoleRules (
  rules: Immutable.List<FilterRule>,
  ruleIndex: number,
  currentRule: FilterRule,
  userRoles: Immutable.List<{name: string, value: string}>
): {
  rules: Immutable.List<FilterRule>,
  index: number
} {
  let extendedRules: Immutable.List<FilterRule> = rules;

  const compareFunc = getCompareStringsFunction(currentRule.operator);
  for (const userRole of userRoles) {
    if (compareFunc(userRole.name, currentRule.value?.toLowerCase() ?? '')) {
      ruleIndex++;
      extendedRules = extendedRules.insert(ruleIndex, FilterRule.createRule('role', userRole.value, currentRule.operator));
      ruleIndex++;
      extendedRules = extendedRules.insert(ruleIndex, FilterRule.createOR());
    }
  }

  return {rules: extendedRules, index: ruleIndex};
}

function _expandUserRoleRule (
  rules: Immutable.List<FilterRule>,
  ruleIndex: number,
  currentRule: FilterRule,
  userRoles: Immutable.List<{name: string, value: string}>
): {
  rules: Immutable.List<FilterRule>,
  index: number
} {
  let extendedRules: Immutable.List<FilterRule> = rules;

  const compareFunc = getCompareStringsFunction(currentRule.operator);

  let newRules: Immutable.List<FilterRule> = Immutable.List();
  for (const userRole of userRoles) {
    if (compareFunc(userRole.name, currentRule.value?.toLowerCase() ?? '')) {
      newRules = newRules.push(FilterRule.createRule('role', userRole.value, currentRule.operator));
      if (newRules.size > 1) {
        newRules = newRules.push(FilterRule.createOR());
      }
    }
  }

  if (newRules.size > 0) {
    extendedRules = extendedRules.set(ruleIndex, newRules.first());
    for (let newStateRulesIndex = 1; newStateRulesIndex < newRules.size; newStateRulesIndex++) {
      ruleIndex++;
      extendedRules = extendedRules.insert(ruleIndex, newRules.get(newStateRulesIndex)!);
    }
  }

  return {rules: extendedRules, index: ruleIndex};
}

function expand (
  filter: Filter,
  acceptedKeywords: string[],
  states: {name: string, value: string}[] | null = null,
  canExpandState: boolean = true
): Filter {
  const shouldExpandState: boolean = canExpandState && acceptedKeywords.includes('state');
  const shouldExpandDuedate: boolean = acceptedKeywords.includes('duedate');
  const shouldExpandActordate: boolean = acceptedKeywords.includes('actordate');
  const shouldExpandCreationdate: boolean = acceptedKeywords.includes('creationdate');
  const shouldExpandModificationdate: boolean = acceptedKeywords.includes('modificationdate');
  const shouldExpandStartedDate: boolean = acceptedKeywords.includes('starteddate');
  const shouldExpandInactivatedDate: boolean = acceptedKeywords.includes('inactivateddate');
  const shouldExpandUserRole: boolean = acceptedKeywords.includes('role');
 
  const stringForUserRole = (role: string): string | null => {
    const stringId: string | null = User.toRoleName(role);
    if (stringId) {
      return translate(stringId);
    }
    return null;
  };

  const userRoles: Immutable.List<{name: string, value: string}> = User.Roles.map((role: any) => {
    if (role !== null && role !== undefined && typeof role === 'string') {
      const possibleTypeString: string | null = stringForUserRole(role);
      if (possibleTypeString) {
        return {name: possibleTypeString.toLowerCase(), value: role};
      }
      return {name: '', value: role};
    }
    return {name: '', value: ''};
  });

  let extendedFilter: Filter = filter;
  for (let ruleIndex: number = 0; ruleIndex < extendedFilter.rules.size; ruleIndex++) {
    const currentRule: FilterRule = extendedFilter.rules.get(ruleIndex)!;

    if (!FilterRule.isAND(currentRule) && !FilterRule.isOR(currentRule)) {
      if (currentRule.key === null) {
        let extendedRules: Immutable.List<FilterRule> = extendedFilter.rules;

        if (shouldExpandState && states) {
          const extendedRulesAndIndex: {rules: Immutable.List<FilterRule>, index: number} = _insertStateRules(extendedRules, ruleIndex, currentRule, states);
          extendedRules = extendedRulesAndIndex.rules;
          ruleIndex = extendedRulesAndIndex.index;
        }

        if (shouldExpandDuedate) {
          const extendedRulesAndIndex = _insertDateRules(extendedRules, ruleIndex, currentRule, 'duedate');
          extendedRules = extendedRulesAndIndex.rules;
          ruleIndex = extendedRulesAndIndex.index;
        }

        if (shouldExpandActordate) {
          const extendedRulesAndIndex = _insertDateRules(extendedRules, ruleIndex, currentRule, 'actordate');
          extendedRules = extendedRulesAndIndex.rules;
          ruleIndex = extendedRulesAndIndex.index;
        }

        if (shouldExpandCreationdate) {
          const extendedRulesAndIndex = _insertDateRules(extendedRules, ruleIndex, currentRule, 'creationdate');
          extendedRules = extendedRulesAndIndex.rules;
          ruleIndex = extendedRulesAndIndex.index;
        }

        if (shouldExpandModificationdate) {
          const extendedRulesAndIndex = _insertDateRules(extendedRules, ruleIndex, currentRule, 'modificationdate');
          extendedRules = extendedRulesAndIndex.rules;
          ruleIndex = extendedRulesAndIndex.index;
        }

        if (shouldExpandStartedDate) {
          const extendedRulesAndIndex = _insertDateRules(extendedRules, ruleIndex, currentRule, 'starteddate');
          extendedRules = extendedRulesAndIndex.rules;
          ruleIndex = extendedRulesAndIndex.index;
        }

        if (shouldExpandInactivatedDate) {
          const extendedRulesAndIndex = _insertDateRules(extendedRules, ruleIndex, currentRule, 'inactivateddata');
          extendedRules = extendedRulesAndIndex.rules;
          ruleIndex = extendedRulesAndIndex.index;
        }

        if (shouldExpandUserRole) {
          const extendedRulesAndIndex = _insertUserRoleRules(extendedRules, ruleIndex, currentRule, userRoles);
          extendedRules = extendedRulesAndIndex.rules;
          ruleIndex = extendedRulesAndIndex.index;
        }

        extendedFilter = extendedFilter.set('rules', extendedRules);
      } else if (shouldExpandState && currentRule.key === 'state' && states) {
        const extendedRulesAndIndex: {rules: Immutable.List<FilterRule>, index: number} = _expandStateRule(extendedFilter.rules, ruleIndex, currentRule, states);
        extendedFilter = extendedFilter.set('rules', extendedRulesAndIndex.rules);
        ruleIndex = extendedRulesAndIndex.index;
      } else if ((shouldExpandDuedate && currentRule.key === 'duedate') ||
      (shouldExpandActordate && currentRule.key === 'actordate') ||
      (shouldExpandCreationdate && currentRule.key === 'creationdate') ||
      (shouldExpandModificationdate && currentRule.key === 'modificationdate') ||
        (shouldExpandStartedDate && currentRule.key === 'starteddate') ||
        (shouldExpandInactivatedDate && currentRule.key === 'inactivateddate')) {
        const extendedRulesAndIndex: {rules: Immutable.List<FilterRule>, index: number} = _expandDateRule(extendedFilter.rules, ruleIndex, currentRule, currentRule.key);
        extendedFilter = extendedFilter.set('rules', extendedRulesAndIndex.rules);
        ruleIndex = extendedRulesAndIndex.index;
      } else if (shouldExpandUserRole && currentRule.key === 'role') {
        const extendedRulesAndIndex: {rules: Immutable.List<FilterRule>, index: number} = _expandUserRoleRule(extendedFilter.rules, ruleIndex, currentRule, userRoles);
        extendedFilter = extendedFilter.set('rules', extendedRulesAndIndex.rules);
        ruleIndex = extendedRulesAndIndex.index;
      }
    }
  }

  return extendedFilter;
}

const FilterSupport = {
  expand
};

export default FilterSupport;
